"use client";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { FaceScanMetaData, FaceScanProps } from "../../types/interfaces";
import {
	generateUuid,
	getPreferredMediaRecorderTypes,
	getRecordingExtension,
	getRecordingMimeType,
	getSupportedMediaRecorderTypes,
	getVideoFPS,
	handleScanTimeCapture,
	handleWebSocketCapture,
} from "../../utils/utils";
import { videoConstraintsExact, videoConstraintsFallback, videoTypes, voiceOverAssetsPath } from "../../utils/constants";
import { getSwanService } from "../../utils/service/swanService";
import speechService from "../../utils/service/speechService";
import useFaceScan from "../../customHooks/useFaceScan";
import usePosthogPageview from "../../customHooks/usePosthogPageview";
import { LanguageKeys } from "../../utils/languageKeys";
import { FaceScanErrorType } from "../../utils/enums";
import LanguageContextProvider from "../../utils/context/languageContext";
import { ConfigProvider } from "../../utils/context/configContext";
import FaceScanStep from "./FaceScanStep";
import ParamsContext from "../../utils/context/paramsContext";

export const FaceScan: React.FC<FaceScanProps> = (props) => {
	const { onScanSuccess, onScanError, onRetry, onScanStart, onCaptureComplete, onUploadStart, onUploadEnd, onMeasurementSocketStart, onMeasurementSocketClose, config, isError, isSuccess } = props;
	const userDetails: FaceScanMetaData = props.userDetails ?? {
		email: "",
		gender: "male",
		deviceFocalLength: 0,
		shopDomain: "",
		bodyScanId: "",
		callbackUrl: null,
	};
	const token = props.token ?? "";
	const webcamRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const recordedBlobsRef = useRef<Blob[] | null>([]);
	const streamRef = useRef<MediaStream | null>(null);
	const recordedFpsRef = useRef<number | null>(null);
	const drawFrameRafRef = useRef<number>(0);
	const recordingVideoElRef = useRef<HTMLVideoElement | null>(null);
	const [showLoader, setShowLoader] = useState(false);
	const [modelReady, setModelReady] = useState(false);
	const [isScanning, setIsScanning] = useState(false);
	const [showGuideCard, setShowGuideCard] = useState(true);
	const [videoLoading, setVideoLoading] = useState(false);
	const [videoError, setVideoError] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [errorType, setErrorType] = useState<FaceScanErrorType | null>(null);
	const [faceScanId, setFaceScanId] = useState(generateUuid());
	const swan = useMemo(() => getSwanService(token), [token]);
	const { email, gender, deviceFocalLength, shopDomain, callbackUrl, bodyScanId } = userDetails;
	const [supportedTypes, setSupportedTypes] = useState<string[]>([]);
	const [currentVideoConstraints, setCurrentVideoConstraints] = useState<MediaTrackConstraints>(videoConstraintsExact);
	const preferredVideoTypes = useMemo(() => getPreferredMediaRecorderTypes(), []);
	const recordingMimeType = getRecordingMimeType(supportedTypes);
	const recordingExtension = getRecordingExtension(recordingMimeType);
	usePosthogPageview();

	const stopRecording = useCallback(() => {
		console.log("Stopping recording...");
		cancelAnimationFrame(drawFrameRafRef.current);
		if (recordingVideoElRef.current) {
			recordingVideoElRef.current.pause();
			recordingVideoElRef.current.srcObject = null;
			recordingVideoElRef.current = null;
		}
		if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
			mediaRecorderRef.current.stop();
		}
		mediaRecorderRef.current = null;
	}, []);

	const uploadFinalVideo = async () => {
		onCaptureComplete?.();
		if (recordedBlobsRef.current) {
			if (recordedBlobsRef.current.length === 0) {
				console.error("No video data recorded");
				setErrorType(FaceScanErrorType.Scan);
				setHasError(true);
				setShowLoader(false);
				return;
			}

			onUploadStart?.();
			setShowLoader(true);
			const videoFile = new File(recordedBlobsRef.current, `${faceScanId}.${recordingExtension}`, {
				type: recordingMimeType,
			});
			// Use FPS captured at recording time, fallback to calculation if not available
			const videoFps = recordedFpsRef.current ?? (await getVideoFPS(videoFile));

			const fileSize = videoFile.size;
			const fileSizeMB = (fileSize / (1024 * 1024)).toFixed(2);
			const estimatedDuration = Math.round(fileSize / 10000);
			const videoData = {
				video_size_mb: parseFloat(fileSizeMB),
				video_size_bytes: fileSize,
				video_fps: videoFps || 0,
				blob_count: recordedBlobsRef.current.length,
				estimated_duration_seconds: estimatedDuration,
			};

			const metaData: any[] = [
				{ gender: gender },
				{ face_scan_id: faceScanId },
				{focal_length: `${deviceFocalLength}`},
				{ customer_store_url: shopDomain },
				{ scan_type: "face_scan" },
				{ body_scan_id: bodyScanId }
			];
			if (callbackUrl) {
				metaData.push({ callback_url: callbackUrl });
			}

			handleScanTimeCapture({
				eventName: `${shopDomain}/face_scan_meta_data`,
				faceScanId,
				email,
				data: JSON.stringify({ metaData, videoData }),
			});

			const uploadStartTime = Date.now();
			handleScanTimeCapture({
				eventName: `${shopDomain}/face_scan_upload_start`,
				faceScanId,
				email,
				startTime: uploadStartTime,
			});

			try {
				const res = await swan.fileUpload.faceScanFileUploader({
					file: videoFile,
					arrayMetaData: metaData,
					objectKey: faceScanId,
					email,
					contentType: videoFile.type,
				});

				const uploadEndTime = Date.now();
				const uploadDuration = uploadEndTime - uploadStartTime;

				handleScanTimeCapture({
					eventName: `${shopDomain}/face_scan_upload_complete`,
					faceScanId,
					email,
					completionTime: uploadEndTime,
					uploadDuration,
				});
				onUploadEnd?.();
				console.log("✅ Upload successful", res);
				swan.measurement.handlFaceScaneSocket({
					onPreopen: () => {
						onMeasurementSocketStart?.();
						handleWebSocketCapture({
							eventName: `${shopDomain}/webSocket`,
							faceScanID: faceScanId,
							connection: "pre_open",
							type: "faceScan_recommendation",
							email,
						});
					},
					faceScanId,
					onOpen: () => {
						handleWebSocketCapture({
							eventName: `${shopDomain}/webSocket`,
							faceScanID: faceScanId,
							connection: "open",
							type: "faceScan_recommendation",
							email,
						});
						console.log("websocket connect open");
					},
					onError: (err) => {
						handleWebSocketCapture({
							eventName: `${shopDomain}/webSocket`,
							faceScanID: faceScanId,
							connection: "error",
							type: "faceScan_recommendation",
							email,
						});
						onError(err);
					},
					onSuccess: (data) => {
						handleWebSocketCapture({
							eventName: `${shopDomain}/webSocket`,
							faceScanID: faceScanId,
							connection: "success",
							type: "faceScan_recommendation",
							email,
						});
						onSuccess(data);
					},
					onClose: () => {
						onMeasurementSocketClose?.();
						console.log("websocket connect close");
						handleWebSocketCapture({
							eventName: `${shopDomain}/webSocket`,
							faceScanID: faceScanId,
							connection: "close",
							type: "faceScan_recommendation",
							email,
						});
					},
				});
			} catch (error) {
				onUploadEnd?.();
				onError(error);
			}
		}
	};

	const startRecording = useCallback(() => {
		const stream = webcamRef.current?.srcObject as MediaStream | null;
		if (!stream) return;

		// Create a canvas to normalize orientation
		const videoTrack = stream.getVideoTracks()[0];
		const settings = videoTrack.getSettings();
		// Fallback to landscape defaults (1280×720); portrait swap below handles orientation
		const { width = 1280, height = 720 } = settings;

		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");

		// Always force portrait (swap width/height if landscape)
		if (width > height) {
			canvas.width = height;
			canvas.height = width;
		} else {
			canvas.width = width;
			canvas.height = height;
		}

		const videoEl = document.createElement("video");
		videoEl.srcObject = stream;
		videoEl.muted = true;
		videoEl.playsInline = true;
		videoEl.play();
		recordingVideoElRef.current = videoEl;

		// Draw video frames onto canvas
		const drawFrame = () => {
			// Rotate if camera gives landscape frames
			if (width > height) {
				ctx?.save();
				ctx?.translate(canvas.width, 0);
				ctx?.rotate(Math.PI / 2);
				ctx?.drawImage(videoEl, 0, 0, height, width);
				ctx?.restore();
			} else {
				ctx?.drawImage(videoEl, 0, 0, width, height);
			}
			drawFrameRafRef.current = requestAnimationFrame(drawFrame);
		};
		drawFrameRafRef.current = requestAnimationFrame(drawFrame);

		// Match recording FPS to actual camera FPS to avoid duplicate frames in the encoded video
		const recordingFps = settings.frameRate ?? 10;
		recordedFpsRef.current = recordingFps; // Store FPS at recording time
		const canvasStream = canvas.captureStream(recordingFps);
		const mediaRecorderOptions: MediaRecorderOptions = {
			videoBitsPerSecond: 1_500_000,
		};
		if (supportedTypes.length > 0) {
			mediaRecorderOptions.mimeType = supportedTypes[0];
		}
		let mediaRecorder;

		try {
			mediaRecorder = new MediaRecorder(canvasStream, mediaRecorderOptions);
		} catch (e) {
			console.error("MediaRecorder init failed:", e);
			setErrorType(FaceScanErrorType.Scan);
			setHasError(true);
			setShowLoader(false);
			return;
		}

		recordedBlobsRef.current = [];
		mediaRecorder.ondataavailable = (event) => {
			if (event?.data && event.data.size > 0 && recordedBlobsRef.current) {
				recordedBlobsRef.current.push(event.data);
			}
		};
		mediaRecorder.onstop = () => {
			console.log("Recording stopped, total blobs:", recordedBlobsRef?.current?.length);
			uploadFinalVideo();
		};

		mediaRecorder.start(100); // 100ms chunks
		mediaRecorderRef.current = mediaRecorder;
		console.log("Recording started successfully (portrait normalized)");
	}, [supportedTypes, uploadFinalVideo]);

	const { faceScanDetector, scanStage, setScanStage, resetScan, isModelLoaded, startScanSequence } = useFaceScan({
		faceScanId,
		shopDomain,
		isError,
		isSuccess,
		onScanComplete: () => {
			stopRecording();
		},
		onModelReady: () => {
			setModelReady(true);
		},
		onStartRecording: () => {
			console.log("Stage 0 completed - starting recording for stages 1-3");
			startRecording();
		},
	});

	const startScan = useCallback(() => {
		if (!isModelLoaded) return;
		onScanStart?.();
		setScanStage(0);
		speechService.playAudio(`${voiceOverAssetsPath}face-scan-vos/Face-forward.mp3`);
		setTimeout(() => {
			setIsScanning(true);
			setTimeout(() => {
				startScanSequence();
			}, 200);
		}, 200);
	}, [setScanStage, setIsScanning, startScanSequence, isModelLoaded, onScanStart]);

	const resetScanState = useCallback(() => {
		setIsScanning(false);
		setShowGuideCard(true);
		stopRecording();
		resetScan();
		onRetry?.();
		recordedBlobsRef.current = [];
		recordedFpsRef.current = null;
		if (mediaRecorderRef.current) {
			mediaRecorderRef.current = null;
		}
		setHasError(false);
		setErrorType(null);
		setScanStage(-1);
		setFaceScanId(generateUuid());
		if (webcamRef.current && streamRef.current) {
			webcamRef.current.srcObject = streamRef.current;
			console.log("Camera stream restored after reset");
		}
	}, [setScanStage, setIsScanning, setShowGuideCard, stopRecording, resetScan, setFaceScanId]);

	const onError = (data: any) => {
		console.log(data, "ws error");
		onScanError?.(data);
		setErrorType(FaceScanErrorType.Scan);
		setHasError(true);
		setShowLoader(false);
		setShowGuideCard(false);
		handleScanTimeCapture({
			eventName: `${shopDomain}/faceScan`,
			faceScanId,
			status: "failed",
			email,
			data: JSON.stringify(data),
		});
	};

	const onSuccess = (data: any) => {
		console.log(data, "ws success");
		if (data && data?.resultType === "intermediate") {
			handleScanTimeCapture({
				eventName: `${shopDomain}/faceScan_success/intermediate`,
				faceScanId,
				status: "success",
				email,
				data: JSON.stringify(data),
			});
		}
		onScanSuccess?.(data);
	};

	useEffect(() => {
		if (isError || isSuccess) return;
		if (navigator.mediaDevices.getUserMedia) {
			navigator.mediaDevices
				.getUserMedia({ video: currentVideoConstraints })
				.then((stream) => {
					streamRef.current = stream;
					if (webcamRef.current) {
						webcamRef.current.srcObject = stream;
					}
				})
				.catch((err) => {
					console.error("Error accessing webcam:", err);
					if (currentVideoConstraints === videoConstraintsExact) {
						setCurrentVideoConstraints(videoConstraintsFallback);
						return;
					}
					const isCameraAccessError = ["NotAllowedError", "PermissionDeniedError", "NotFoundError", "DevicesNotFoundError", "NotReadableError", "TrackStartError"].includes(err?.name);
					setErrorType(isCameraAccessError ? FaceScanErrorType.Camera : FaceScanErrorType.Scan);
					setHasError(true);
				});
		}
		return () => {
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
			}
		};
	}, [isError, isSuccess, currentVideoConstraints]);

	// Face detection interval - only run when scanning is active
	useEffect(() => {
		if (!modelReady || !isScanning) return;
		const intervalId = setInterval(() => {
			faceScanDetector(webcamRef, canvasRef);
		}, 500);

		// eslint-disable-next-line consistent-return
		return () => clearInterval(intervalId);
	}, [faceScanDetector, modelReady, isScanning]);

	const getButtonText = () => {
		if (isScanning) return LanguageKeys.reset;
		if (!isModelLoaded) return LanguageKeys.loading;
		return LanguageKeys.start;
	};

	console.log("Model ready:", modelReady, "Has error:", hasError, "Is scanning:", isScanning, "showLoader", showLoader);
	useEffect(() => {
		const supported = getSupportedMediaRecorderTypes(preferredVideoTypes, videoTypes);
		setSupportedTypes(supported);
	}, [preferredVideoTypes]);
	useEffect(() => {
		setScanStage(-1);
	}, []);

	return (
		<div data-face-scan-stage={scanStage}>
		<LanguageContextProvider>
			<ConfigProvider config={config}>
				<ParamsContext.Provider
					value={{
						isError,
						isSuccess,
						showLoader,
						hasError,
						errorType,
						resetScanState,
						showGuideCard,
						scanStage,
						videoLoading,
						setVideoLoading,
						videoError,
						setVideoError,
						gender,
						getButtonText,
						isScanning,
						startScan,
						isModelLoaded,
						onRetry,
					}}
				>
					<FaceScanStep webcamRef={webcamRef} canvasRef={canvasRef} />
				</ParamsContext.Provider>
			</ConfigProvider>
		</LanguageContextProvider>
		</div>
	);
};
